import { useEffect, useMemo, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Search,
  Download,
  Eye,
  Play,
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  Pause,
  VideoOff,
  MoreVertical,
  Edit,
} from "lucide-react";
import { toast } from "sonner";
import { courseService } from "@/services/course";
import { Course, STATUS_OPTIONS } from "@/types/course.types";
import { Textarea } from "@/components/ui/textarea";

import { useNavigate } from "react-router-dom";

// 查询字段类型
type QueryField = "title" | "articleId";

export default function CourseManagement() {
  // 路由信息-返回使用
  const navigate = useNavigate();

  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // 数据
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [total, setTotal] = useState(0);

  // 1) 条件查询：字段 + 输入
  const [field, setField] = useState<QueryField>("title");
  const [keyword, setKeyword] = useState("");

  // 2) 状态筛选
  const [status, setStatus] = useState<string>("all");

  // 3) 蓝思值范围
  const [lexileMin, setLexileMin] = useState<string>("");
  const [lexileMax, setLexileMax] = useState<string>("");

  // 导出弹窗相关状态
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [processingExport, setProcessingExport] = useState(false);

  // 停用/启用课程弹窗相关状态
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [disableReason, setDisableReason] = useState("");
  const [processingStatusUpdate, setProcessingStatusUpdate] = useState(false);

  // 详情弹窗状态
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // 封面查看弹窗状态
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [currentCoverUrl, setCurrentCoverUrl] = useState("");

  // 视频播放器相关状态
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: any = {
      page,
      page_size: pageSize,
    };

    // 条件查询
    const kw = keyword.trim();
    if (kw) {
      params[field] = kw; // 根据选择的字段设置参数
    }

    // 状态筛选
    if (status !== "all") {
      params.status = parseInt(status);
    }

    // 蓝思值范围
    if (lexileMin) {
      params.min_blue_score = parseInt(lexileMin);
    }
    if (lexileMax) {
      params.max_blue_score = parseInt(lexileMax);
    }

    return params;
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = await courseService.getCourses(params);

      // 更新数据
      const list = (res as any).list ?? [];
      const totalCount = (res as any).total ?? 0;

      setCourses(list);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取课程列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchCourses();
  };

  const resetFilters = () => {
    setField("title");
    setKeyword("");
    setStatus("all");
    setLexileMin("");
    setLexileMax("");
    setPage(1);
  };

  // 打开导出确认弹窗
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  // 执行实际导出操作
  const confirmExport = async () => {
    setProcessingExport(true);
    try {
      // 构建请求参数
      const params = buildRequestParams();

      // 调用导出接口
      const blob = await courseService.exportCourses(params);

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `课程列表_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();

      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("导出成功", {
        description: "课程列表已成功导出",
      });

      // 关闭弹窗
      setExportDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("导出失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingExport(false);
    }
  };

  // 打开课程状态操作弹窗
  const openStatusDialog = (course: Course) => {
    setSelectedCourse(course);
    setDisableReason(course.stopedReasion || "");
    setStatusDialogOpen(true);
  };

  // 更新课程状态
  const updateCourseStatus = async () => {
    if (!selectedCourse) return;

    setProcessingStatusUpdate(true);
    try {
      if (selectedCourse.status === 1) {
        // 从正常状态切换到停用状态
        await courseService.disableCourse(selectedCourse.id, disableReason);

        toast.success("课程已停用", {
          description: `课程 "${selectedCourse.title}" 已成功停用`,
        });
      } else {
        // 从停用状态切换到正常状态
        await courseService.enableCourse(selectedCourse.id);

        toast.success("课程已启用", {
          description: `课程 "${selectedCourse.title}" 已成功启用`,
        });
      }

      // 关闭弹窗并刷新数据
      setStatusDialogOpen(false);
      fetchCourses();
    } catch (e) {
      console.error(e);
      toast.error("更新课程状态失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingStatusUpdate(false);
    }
  };

  // 查看课程详情
  const viewCourseDetails = (course: Course) => {
    setSelectedCourse(course);
    setDetailDialogOpen(true);
  };

  // 查看封面大图
  const viewCover = (coverUrl: string) => {
    setCurrentCoverUrl(formatCoverUrl(coverUrl));
    setCoverDialogOpen(true);
  };

  // 打开视频播放器
  const openVideoPlayer = (url: string) => {
    if (!url) {
      toast.error("无视频地址");
      return;
    }

    setCurrentVideoUrl(formatVideoUrl(url));
    setVideoDialogOpen(true);
    setIsPlaying(true);
  };

  // 格式化视频URL (确保完整路径)
  const formatVideoUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${import.meta.env.VITE_OSS_HOST}assets/video/${url}`;
  };

  // 处理视频播放/暂停
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }

    setIsPlaying(!isPlaying);
  };

  // 处理视频静音/取消静音
  const toggleMute = () => {
    if (!videoRef.current) return;

    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  // 处理全屏
  const toggleFullscreen = () => {
    if (!videoContainerRef.current) return;

    if (!isFullscreen) {
      if (videoContainerRef.current.requestFullscreen) {
        videoContainerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // 跳转到详情界面
  const gotoDetail = (id: string) => {
    navigate(`/courses/detail?id=${id}`);
  };

  // 监听全屏变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // 关闭视频弹窗时暂停视频
  useEffect(() => {
    if (!videoDialogOpen && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [videoDialogOpen]);

  // 格式化时间显示
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr || dateStr === "0001-01-01T00:00:00Z") return "-";
    return new Date(dateStr).toLocaleString();
  };

  // 格式化学习时间（毫秒转为分钟）
  const formatStudyTime = (milliseconds: number) => {
    if (!milliseconds) return "-";
    const seconds = Math.floor(milliseconds / 1000);
    if (seconds < 60) return `${seconds}秒`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}分钟`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}小时${mins}分钟` : `${hours}小时`;
  };

  // 格式化封面URL (确保完整路径)
  const formatCoverUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // 假设有一个基础URL前缀
    return `${import.meta.env.VITE_OSS_HOST}/Cover_pics/${url}`;
  };

  // 获取单词数量
  const getWordCount = (words: string) => {
    if (!words) return 0;
    return words.split(",").length;
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">课程管理</h2>
      </div>

      {/* 筛选区 - 分为两行 */}
      <div className="rounded-lg border p-4 space-y-4">
        {/* 第一行 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 1. 条件查询（课程名称或ID） */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">条件查询</label>
            <div className="flex gap-2">
              <Select
                value={field}
                onValueChange={(val: QueryField) => setField(val)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="选择字段" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="title">课程名称</SelectItem>
                  <SelectItem value="articleId">课程ID</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder={
                  field === "title" ? "请输入课程名称" : "请输入课程ID"
                }
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
            </div>
          </div>

          {/* 2. 状态筛选 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">状态</label>
            <Select
              value={status}
              onValueChange={(val: string) => setStatus(val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="选择状态" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 第二行 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 3. 蓝思值范围 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">蓝思值范围</label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="最小值"
                value={lexileMin}
                onChange={(e) => setLexileMin(e.target.value)}
                className="flex-1"
              />
              <Input
                type="number"
                placeholder="最大值"
                value={lexileMax}
                onChange={(e) => setLexileMax(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* 4. 操作按钮组 */}
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
              <Button
                className="w-24"
                variant="secondary"
                onClick={handleExport}
              >
                <Download className="mr-2 h-4 w-4" />
                导出
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 列表：可滚动容器 */}
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">ID</TableHead>
              <TableHead className="whitespace-nowrap">封面</TableHead>
              <TableHead className="whitespace-nowrap">课程名称</TableHead>
              <TableHead className="whitespace-nowrap">蓝思值</TableHead>
              <TableHead className="whitespace-nowrap">学新词</TableHead>
              <TableHead className="whitespace-nowrap">学习时间</TableHead>
              <TableHead className="whitespace-nowrap">动画地址</TableHead>
              <TableHead className="whitespace-nowrap">入库时间</TableHead>
              <TableHead className="whitespace-nowrap">状态</TableHead>
              <TableHead className="whitespace-nowrap">停用理由</TableHead>
              <TableHead className="whitespace-nowrap">停用时间</TableHead>
              <TableHead className="whitespace-nowrap">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : courses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="h-24 text-center">
                  没有找到课程记录
                </TableCell>
              </TableRow>
            ) : (
              courses.map((course) => (
                <TableRow key={course.id}>
                  <TableCell className="whitespace-nowrap">
                    {course.articleId}
                  </TableCell>
                  <TableCell>
                    <div className="h-12 w-16 relative">
                      <img
                        src={formatCoverUrl(course.coverUrl)}
                        alt={course.title}
                        className="h-full w-full object-cover rounded cursor-pointer"
                        onClick={() => viewCover(course.coverUrl)}
                      />
                    </div>
                  </TableCell>
                  <TableCell
                    className="max-w-[150px] truncate"
                    title={course.title}
                  >
                    {course.title}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {course.lansiRealShow}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {getWordCount(course.words)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatStudyTime(course.toalDuration)}
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    {course.videoUrl ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="text-blue-600 underline cursor-pointer hover:text-blue-800 truncate flex-1 text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              formatVideoUrl(course.videoUrl)
                            );
                            toast.success("已复制完整链接");
                          }}
                          title={course.videoUrl}
                        >
                          {course.videoUrl}
                        </span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex-shrink-0"
                                onClick={() => openVideoPlayer(course.videoUrl)}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>播放视频</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm flex-1">
                          无视频
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full bg-gray-100 opacity-50 cursor-not-allowed flex-shrink-0"
                          disabled
                        >
                          <VideoOff className="h-4 w-4 text-gray-400" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(course.created_at)}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        course.status === 1
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {course.status === 1 ? "正常" : "停用"}
                    </span>
                  </TableCell>
                  <TableCell
                    className="max-w-[150px] truncate"
                    title={course.stopedReasion}
                  >
                    {course.stopedReasion || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(course.stoped_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="whitespace-nowrap"
                              onClick={() => openStatusDialog(course)}
                            >
                              {course.status === 1 ? (
                                <p className="h-4 w-4 text-red-500">停用</p>
                              ) : (
                                <p className="h-4 w-4 text-green-500">启用</p>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {course.status === 1 ? "停用课程" : "启用课程"}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div></div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="h-8 w-8 p-0 hover:bg-muted/60 rounded-full"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          sideOffset={4}
                          className="w-40 rounded-xl shadow-lg border bg-white"
                        >
                          <DropdownMenuLabel className="text-xs text-muted-foreground">
                            相关操作
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => viewCourseDetails(course)}
                            className="cursor-pointer"
                          >
                            <Eye className="mr-2 h-4 w-4 text-emerald-600" />
                            查看详情
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => gotoDetail(course.id)}
                            className="cursor-pointer"
                          >
                            <Edit className="mr-2 h-4 w-4 text-indigo-600" />
                            编辑课程
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* 导出确认弹窗 */}
      <Dialog
        open={exportDialogOpen}
        onOpenChange={(open) => !open && setExportDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>确认导出课程列表</DialogTitle>
            <DialogDescription>
              将当前筛选条件下的课程列表导出为Excel文件
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">导出记录数量:</span>
                <span className="font-medium text-blue-600">{total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">筛选条件:</span>
                <span className="text-gray-600">
                  {keyword
                    ? `${field === "title" ? "课程名称" : "课程ID"}: ${keyword}`
                    : `无特定${field === "title" ? "课程名称" : "课程ID"}`}
                </span>
              </div>
              {status !== "all" && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">状态:</span>
                  <span className="text-gray-600">
                    {status === "1" ? "正常" : "停用"}
                  </span>
                </div>
              )}
              {lexileMin && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">最小蓝思值:</span>
                  <span className="text-gray-600">{lexileMin}</span>
                </div>
              )}
              {lexileMax && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">最大蓝思值:</span>
                  <span className="text-gray-600">{lexileMax}</span>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-500">
              导出的Excel文件将包含所有当前筛选条件下的课程数据
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={confirmExport} disabled={processingExport}>
              {processingExport ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  导出中...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  确认导出
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 课程状态更新弹窗 */}
      {selectedCourse && (
        <Dialog
          open={statusDialogOpen}
          onOpenChange={(open) => !open && setStatusDialogOpen(false)}
        >
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle>
                {selectedCourse.status === 1 ? "停用课程" : "启用课程"}
              </DialogTitle>
              <DialogDescription>
                {selectedCourse.status === 1
                  ? "停用后该课程将不再对用户可见"
                  : "启用后该课程将对用户可见"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">课程ID:</span>
                  <span className="text-gray-600">
                    {selectedCourse.articleId}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">课程名称:</span>
                  <span className="text-gray-600">{selectedCourse.title}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">当前状态:</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedCourse.status === 1
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedCourse.status === 1 ? "正常" : "停用"}
                  </span>
                </div>
              </div>

              {selectedCourse.status === 1 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">停用理由:</label>
                  <Textarea
                    placeholder="请输入停用理由"
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <Button
                onClick={updateCourseStatus}
                disabled={
                  processingStatusUpdate ||
                  (selectedCourse.status === 1 && !disableReason.trim())
                }
                variant={
                  selectedCourse.status === 1 ? "destructive" : "default"
                }
              >
                {processingStatusUpdate ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                    处理中...
                  </>
                ) : selectedCourse.status === 1 ? (
                  "确认停用"
                ) : (
                  "确认启用"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* 课程详情弹窗 */}
      {selectedCourse && (
        <Dialog
          open={detailDialogOpen}
          onOpenChange={(open) => !open && setDetailDialogOpen(false)}
        >
          <DialogContent className="sm:max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle>课程详情</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <img
                  src={formatCoverUrl(selectedCourse.coverUrl)}
                  alt={selectedCourse.title}
                  className="w-full h-auto rounded-lg object-cover cursor-pointer"
                  onClick={() => viewCover(selectedCourse.coverUrl)}
                />

                {selectedCourse.videoUrl && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => openVideoPlayer(selectedCourse.videoUrl)}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      播放视频
                    </Button>
                  </div>
                )}
              </div>

              <div className="md:col-span-2 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">ID</h4>
                    <p>{selectedCourse.articleId}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">状态</h4>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedCourse.status === 1
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {selectedCourse.status === 1 ? "正常" : "停用"}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <h4 className="text-sm font-medium text-gray-500">
                      课程名称
                    </h4>
                    <p className="font-medium text-lg">
                      {selectedCourse.title}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      蓝思值(真实/展示)
                    </h4>
                    <p>
                      {selectedCourse.lansiRealShow} /
                      {selectedCourse.lansiOvertShow}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      学新词
                    </h4>
                    <p>{getWordCount(selectedCourse.words)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      学习时间
                    </h4>
                    <p>{formatStudyTime(selectedCourse.duration)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      总学习时间
                    </h4>
                    <p>{formatStudyTime(selectedCourse.toalDuration)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">类型</h4>
                    <p>{selectedCourse.isFiction === 1 ? "虚构" : "非虚构"}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">主题</h4>
                    <p>
                      {selectedCourse.topic} ({selectedCourse.topicCode})
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">级别</h4>
                    <p>{selectedCourse.level}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      入库时间
                    </h4>
                    <p>{formatDateTime(selectedCourse.created_at)}</p>
                  </div>

                  {selectedCourse.status === 2 && (
                    <>
                      <div className="col-span-2">
                        <h4 className="text-sm font-medium text-gray-500">
                          停用理由
                        </h4>
                        <p>{selectedCourse.stopedReasion || "-"}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          停用时间
                        </h4>
                        <p>{formatDateTime(selectedCourse.stoped_at)}</p>
                      </div>
                    </>
                  )}
                </div>

                {selectedCourse.desc && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      课程描述
                    </h4>
                    <p className="text-sm text-gray-700">
                      {selectedCourse.desc}
                    </p>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    单词列表
                  </h4>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedCourse.words.split(",").map((word, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 px-2 py-1 rounded text-xs"
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant={
                  selectedCourse.status === 1 ? "destructive" : "default"
                }
                onClick={() => {
                  setDetailDialogOpen(false);
                  openStatusDialog(selectedCourse);
                }}
              >
                {selectedCourse.status === 1 ? "停用课程" : "启用课程"}
              </Button>
              <DialogClose asChild>
                <Button>关闭</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* 封面查看弹窗 */}
      <Dialog
        open={coverDialogOpen}
        onOpenChange={(open) => !open && setCoverDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-3xl bg-white p-0">
          <div className="relative">
            <img
              src={currentCoverUrl}
              alt="封面大图"
              className="w-full h-auto"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* 视频播放弹窗 */}
      <Dialog
        open={videoDialogOpen}
        onOpenChange={(open) => !open && setVideoDialogOpen(false)}
        modal={true}
      >
        <DialogContent className="sm:max-w-4xl bg-black p-0 overflow-hidden">
          <div className="relative" ref={videoContainerRef}>
            <video
              ref={videoRef}
              src={currentVideoUrl}
              className="w-full h-auto"
              controls={false}
              autoPlay
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />

            {/* 自定义控制器 */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={togglePlay}
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={toggleMute}
                  >
                    {isMuted ? (
                      <VolumeX className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                  </Button>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? (
                    <Minimize className="h-5 w-5" />
                  ) : (
                    <Maximize className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {!loading && courses.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 条课程记录，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
